// ============================================================
// Datei: frontend/src/components/BacktestTab.tsx
// ============================================================
// Backtest-/Analyse-Dashboard im Sheet-Style. Ein Endpoint
// (/api/backtest/:account_id) liefert den kompletten Report;
// die Trades-Tabelle lädt zusätzlich die Roh-Trades.
// Deutsche Zahlenformatierung im UI, intern float/Punkt.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { backtestApi, tradesApi, BacktestReport, BtGroupStat, Trade } from '../services/api';

// ── deutsche Zahlenformatierung ──────────────────────────────────────
const nf = (n: number | null | undefined, d = 2) =>
  (n ?? 0).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
const pct = (n: number | null | undefined, d = 1) => `${nf(n, d)} %`;
const money = (n: number | null | undefined) => `${nf(n, 2)} $`;
const ratio = (n: number | null | undefined) =>
  (n ?? 0) >= 999 ? '∞' : nf(n, 2);

type SubTab =
  | 'overview' | 'auswertung' | 'zeit' | 'faktoren'
  | 'trades' | 'equity' | 'cluster' | 'monatlich';

type RangeKey = '7D' | '30D' | '90D' | '180D' | 'ALL' | 'custom';

const SUB_TABS: [SubTab, string][] = [
  ['overview', 'Übersicht'],
  ['auswertung', 'Auswertung'],
  ['zeit', 'Zeit'],
  ['faktoren', 'Faktoren'],
  ['trades', 'Trades'],
  ['equity', 'Equity'],
  ['cluster', 'Cluster'],
  ['monatlich', 'Monatlich'],
];

// ── Styles (Sheet-Look, dunkles Theme) ───────────────────────────────
const s: Record<string, React.CSSProperties> = {
  wrap: { color: '#e2e8f0' },
  toolbar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 },
  rangeBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  rangeBtnActive: { background: '#22c55e22', border: '1px solid #22c55e', color: '#22c55e' },
  dateInput: { background: '#0f1424', border: '1px solid #334155', color: '#e2e8f0', padding: '5px 8px', borderRadius: 8, fontSize: 13 },
  subnav: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid #1e2539', paddingBottom: 8 },
  subBtn: { background: 'transparent', border: '1px solid transparent', color: '#94a3b8', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  subBtnActive: { background: '#13172b', border: '1px solid #1e2539', color: '#fff' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 },
  kpiCard: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 12, padding: '14px 16px' },
  kpiLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
  kpiValue: { fontSize: 22, fontWeight: 700 },
  kpiSub: { fontSize: 12, color: '#64748b', marginTop: 4 },
  card: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 16, marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#e2e8f0' },
  tableWrap: { overflowX: 'auto', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid #1e2539', whiteSpace: 'nowrap', background: '#13172b' },
  thLeft: { textAlign: 'left', padding: '8px 10px', color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid #1e2539', whiteSpace: 'nowrap', background: '#13172b' },
  td: { textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid #161b30', whiteSpace: 'nowrap' },
  tdLeft: { textAlign: 'left', padding: '7px 10px', borderBottom: '1px solid #161b30', whiteSpace: 'nowrap' },
  badge: { display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, marginLeft: 6, verticalAlign: 'middle' },
  ci: { color: '#64748b', fontSize: 11, marginLeft: 6 },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#64748b' },
  spinner: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8' },
  errorBox: { background: '#ef444411', border: '1px solid #ef444444', color: '#fca5a5', padding: 16, borderRadius: 12 },
  select: { background: '#0f1424', border: '1px solid #334155', color: '#e2e8f0', padding: '5px 8px', borderRadius: 8, fontSize: 13 },
};

const green = '#22c55e';
const red = '#ef4444';
const pnlColor = (v: number) => (v >= 0 ? green : red);

// ── Hilfskomponenten ─────────────────────────────────────────────────

const Kpi: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <div style={s.kpiCard}>
    <div style={s.kpiLabel}>{label}</div>
    <div style={{ ...s.kpiValue, color: color || '#e2e8f0' }}>{value}</div>
    {sub && <div style={s.kpiSub}>{sub}</div>}
  </div>
);

/** WR-Zelle mit 95%-KI; markiert „nicht signifikant", wenn KI die Baseline-WR überlappt. */
const WrCell: React.FC<{ g: BtGroupStat; baseline: number }> = ({ g, baseline }) => {
  const notSig = baseline >= g.wr_ci_low && baseline <= g.wr_ci_high;
  return (
    <span>
      <strong style={{ color: g.wr >= 50 ? green : '#e2e8f0' }}>{pct(g.wr)}</strong>
      <span style={s.ci}>[{nf(g.wr_ci_low, 0)}–{nf(g.wr_ci_high, 0)}]</span>
      {notSig && (
        <span style={{ ...s.badge, background: '#64748b22', color: '#94a3b8' }} title="95%-KI überlappt die Gesamt-Baseline-WR">
          n. sig.
        </span>
      )}
    </span>
  );
};

/** Gruppentabelle (Asset/Session/Direction), sortiert nach Expectancy. */
const GroupTable: React.FC<{ title: string; rows: BtGroupStat[]; baseline: number; keyLabel: string }> = ({ title, rows, baseline, keyLabel }) => (
  <div style={s.card}>
    <div style={s.cardTitle}>{title}</div>
    {rows.length === 0 ? (
      <div style={s.empty}>Keine Daten im Zeitfenster.</div>
    ) : (
      <div style={s.tableWrap}>
        <div className="table-scroll">
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.thLeft}>{keyLabel}</th>
              <th style={s.th}>n</th>
              <th style={s.th}>WR % (95%-KI)</th>
              <th style={s.th}>PnL</th>
              <th style={s.th}>Ø PnL/Trade</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.key}>
                <td style={s.tdLeft}><strong>{g.key}</strong></td>
                <td style={s.td}>{g.n}</td>
                <td style={s.td}><WrCell g={g} baseline={baseline} /></td>
                <td style={{ ...s.td, color: pnlColor(g.pnl) }}>{money(g.pnl)}</td>
                <td style={{ ...s.td, color: pnlColor(g.expectancy), fontWeight: 700 }}>{money(g.expectancy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    )}
  </div>
);

// ── Hauptkomponente ──────────────────────────────────────────────────

const BacktestTab: React.FC<{ accountId: string }> = ({ accountId }) => {
  const [sub, setSub] = useState<SubTab>('overview');
  const [range, setRange] = useState<RangeKey>('ALL');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [report, setReport] = useState<BacktestReport | null>(null);
  const [allTrades, setAllTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Trades-Tab Filter + Sortierung
  const [fAsset, setFAsset] = useState('');
  const [fSession, setFSession] = useState('');
  const [fDir, setFDir] = useState('');
  const [sortField, setSortField] = useState<string>('ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const window = useMemo((): { from?: string; to?: string } => {
    if (range === 'ALL') return {};
    if (range === 'custom') {
      return {
        from: customFrom ? new Date(customFrom + 'T00:00:00Z').toISOString() : undefined,
        to: customTo ? new Date(customTo + 'T23:59:59Z').toISOString() : undefined,
      };
    }
    const days: Record<string, number> = { '7D': 7, '30D': 30, '90D': 90, '180D': 180 };
    const to = new Date();
    const from = new Date(Date.now() - days[range] * 86400000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [range, customFrom, customTo]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const [rep, trd] = await Promise.all([
        backtestApi.get(accountId, window.from, window.to),
        tradesApi.list({ account_id: accountId }),
      ]);
      setReport(rep.data);
      setAllTrades(trd.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.toString?.() || e?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [accountId, window.from, window.to]);

  useEffect(() => { load(); }, [load]);

  // Trades im Zeitfenster (client-seitig nach entry_time/trade_date filtern)
  const windowTrades = useMemo(() => {
    const fromMs = window.from ? Date.parse(window.from) : -Infinity;
    const toMs = window.to ? Date.parse(window.to) : Infinity;
    return allTrades.filter((t) => {
      const ts = Date.parse(t.trade_date);
      return ts >= fromMs && ts <= toMs;
    });
  }, [allTrades, window.from, window.to]);

  const tradeFilterOptions = useMemo(() => ({
    assets: Array.from(new Set(windowTrades.map((t) => (t.asset || '').trim().toUpperCase()))).filter(Boolean).sort(),
    sessions: Array.from(new Set(windowTrades.map((t) => (t.session || '').trim().toUpperCase()))).filter(Boolean).sort(),
    dirs: Array.from(new Set(windowTrades.map((t) => ((t as any).direction || 'long').toLowerCase()))).filter(Boolean).sort(),
  }), [windowTrades]);

  const filteredSortedTrades = useMemo(() => {
    let rows = windowTrades.filter((t) =>
      (!fAsset || (t.asset || '').trim().toUpperCase() === fAsset) &&
      (!fSession || (t.session || '').trim().toUpperCase() === fSession) &&
      (!fDir || ((t as any).direction || 'long').toLowerCase() === fDir)
    );
    const val = (t: any, f: string) => {
      if (f === 'ts') return Date.parse(t.trade_date) || 0;
      const v = t[f];
      return typeof v === 'number' ? v : (v ?? '');
    };
    rows = [...rows].sort((a, b) => {
      const va = val(a, sortField), vb = val(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [windowTrades, fAsset, fSession, fDir, sortField, sortDir]);

  const toggleSort = (f: string) => {
    if (sortField === f) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(f); setSortDir('desc'); }
  };

  // ── Render ─────────────────────────────────────────────────────────
  const rangeButtons: [RangeKey, string][] = [
    ['7D', '7T'], ['30D', '30T'], ['90D', '90T'], ['180D', '180T'], ['ALL', 'Alle'], ['custom', 'Custom'],
  ];

  return (
    <div style={s.wrap}>
      {/* Zeitfenster-Selektor */}
      <div style={s.toolbar}>
        {rangeButtons.map(([k, lbl]) => (
          <button key={k} style={{ ...s.rangeBtn, ...(range === k ? s.rangeBtnActive : {}) }} onClick={() => setRange(k)}>
            {lbl}
          </button>
        ))}
        {range === 'custom' && (
          <>
            <input type="date" style={s.dateInput} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span style={{ color: '#64748b' }}>–</span>
            <input type="date" style={s.dateInput} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
        <button style={s.rangeBtn} onClick={load}>↻ Aktualisieren</button>
        {report && <span style={{ color: '#475569', fontSize: 12, marginLeft: 'auto' }}>Stand: {new Date(report.data_as_of).toLocaleString('de-DE')}</span>}
      </div>

      {/* Sub-Tabs */}
      <div style={s.subnav}>
        {SUB_TABS.map(([k, lbl]) => (
          <button key={k} style={{ ...s.subBtn, ...(sub === k ? s.subBtnActive : {}) }} onClick={() => setSub(k)}>
            {lbl}
          </button>
        ))}
      </div>

      {/* States */}
      {loading && <div style={s.spinner}>⏳ Lade Backtest-Report…</div>}
      {!loading && error && <div style={s.errorBox}>⚠ {error}</div>}
      {!loading && !error && report && report.kpis.trades === 0 && (
        <div style={s.empty}>📭 Keine Trades im gewählten Zeitfenster.</div>
      )}

      {!loading && !error && report && report.kpis.trades > 0 && (
        <>
          {sub === 'overview' && <Overview report={report} />}
          {sub === 'auswertung' && <Auswertung report={report} />}
          {sub === 'zeit' && <Zeit report={report} />}
          {sub === 'faktoren' && <Faktoren report={report} />}
          {sub === 'cluster' && <Cluster report={report} />}
          {sub === 'monatlich' && <Monatlich report={report} />}
          {sub === 'equity' && <Equity report={report} />}
          {sub === 'trades' && (
            <Trades
              rows={filteredSortedTrades}
              opts={tradeFilterOptions}
              fAsset={fAsset} fSession={fSession} fDir={fDir}
              setFAsset={setFAsset} setFSession={setFSession} setFDir={setFDir}
              sortField={sortField} sortDir={sortDir} toggleSort={toggleSort}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Sub-Tab: Übersicht ───────────────────────────────────────────────
const Overview: React.FC<{ report: BacktestReport }> = ({ report }) => {
  const k = report.kpis;
  return (
    <div style={s.kpiGrid}>
      <Kpi label="Trades" value={`${k.trades}`} sub={`${k.wins} W / ${k.losses} L`} />
      <Kpi label="Win Rate" value={pct(k.wr)} color={k.wr >= 50 ? green : red} />
      <Kpi label="Gesamt-PnL" value={money(k.total_pnl)} color={pnlColor(k.total_pnl)} />
      <Kpi label="Profit Factor" value={ratio(k.profit_factor)} color={k.profit_factor >= 1.5 ? green : k.profit_factor < 1 ? red : undefined} />
      <Kpi label="Payoff-Ratio" value={ratio(k.payoff_ratio)} color={k.payoff_ratio >= 2 ? green : undefined} />
      <Kpi label="Expectancy/Trade" value={money(k.expectancy)} color={pnlColor(k.expectancy)} />
      <Kpi label="Ø Win" value={money(k.avg_win)} color={green} />
      <Kpi label="Ø Loss" value={money(k.avg_loss)} color={red} />
      <Kpi label="Max. Drawdown" value={pct(k.max_dd_pct)} color={red} />
      <Kpi label="Längste Win-Serie" value={`${k.max_win_streak}`} color={green} />
      <Kpi label="Längste Loss-Serie" value={`${k.max_loss_streak}`} color={red} />
      <Kpi label="Längste Underwater-Periode" value={`${k.longest_underwater} Trades`} color={k.longest_underwater > 0 ? red : undefined} />
    </div>
  );
};

// ── Sub-Tab: Auswertung ──────────────────────────────────────────────
const Auswertung: React.FC<{ report: BacktestReport }> = ({ report }) => {
  const baseline = report.kpis.wr;
  return (
    <>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
        Sortiert nach Ø PnL/Trade (Expectancy). Baseline-WR: {pct(baseline)}. „n. sig." = 95%-KI überlappt die Baseline.
      </div>
      <GroupTable title="Nach Asset" rows={report.per_asset} baseline={baseline} keyLabel="Asset" />
      <GroupTable title="Nach Session" rows={report.per_session} baseline={baseline} keyLabel="Session" />
      <GroupTable title="Nach Direction" rows={report.per_direction} baseline={baseline} keyLabel="Direction" />
      {report.per_setup.length > 0 && (
        <GroupTable title="Nach Setup" rows={report.per_setup} baseline={baseline} keyLabel="Setup" />
      )}
    </>
  );
};

// ── Sub-Tab: Zeit ────────────────────────────────────────────────────
const Zeit: React.FC<{ report: BacktestReport }> = ({ report }) => {
  // schwächster Block (niedrigste Expectancy) unter den befüllten Blöcken
  const filled = report.hourly_block.filter((b) => b.n > 0);
  const worstExp = filled.length ? Math.min(...filled.map((b) => b.expectancy)) : Infinity;
  return (
    <>
      <div style={s.card}>
        <div style={s.cardTitle}>4h-Blöcke</div>
        <div style={s.tableWrap}>
          <div className="table-scroll">
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.thLeft}>Block</th>
                <th style={s.th}>n</th>
                <th style={s.th}>WR %</th>
                <th style={s.th}>PnL</th>
                <th style={s.th}>Ø PnL/Trade</th>
              </tr>
            </thead>
            <tbody>
              {report.hourly_block.map((b) => {
                const worst = b.n > 0 && b.expectancy === worstExp;
                return (
                  <tr key={b.block} style={worst ? { background: '#ef444411' } : undefined}>
                    <td style={s.tdLeft}>
                      <strong>{b.block}</strong>
                      {worst && <span style={{ ...s.badge, background: '#ef444422', color: red }}>schwächster</span>}
                    </td>
                    <td style={s.td}>{b.n}</td>
                    <td style={s.td}>{b.n > 0 ? pct(b.wr) : '—'}</td>
                    <td style={{ ...s.td, color: pnlColor(b.pnl) }}>{money(b.pnl)}</td>
                    <td style={{ ...s.td, color: pnlColor(b.expectancy), fontWeight: 700 }}>{b.n > 0 ? money(b.expectancy) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>Trade-Verteilung nach Stunde (UTC)</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={report.distribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis dataKey="hour" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(h) => `${h}h`} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
              formatter={(v: any) => [`${v} Trades`, 'Anzahl']}
              labelFormatter={(l: any) => `${l}:00–${l}:59 UTC`}
            />
            <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

// ── Sub-Tab: Faktoren ────────────────────────────────────────────────
const FACTOR_LABELS: Record<string, string> = {
  aligned: 'Aligned (Delta dreht beim Fill)',
  candles_before_fill: 'Candles Before Fill',
  mae_pct_sl: 'MAE % vom Stop',
  mfi: 'MFI',
  duration: 'Trade-Dauer',
  delta_fill: 'Delta beim Fill',
};
const Faktoren: React.FC<{ report: BacktestReport }> = ({ report }) => {
  // Faktoren paarweise gruppieren (Reihenfolge wie geliefert)
  const order: string[] = [];
  const byFactor: Record<string, typeof report.factors> = {};
  report.factors.forEach((f) => {
    if (!byFactor[f.factor]) { byFactor[f.factor] = []; order.push(f.factor); }
    byFactor[f.factor].push(f);
  });
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Gewinner- vs. Verlierer-Faktoren</div>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
        Pro Faktor beide Gruppen nebeneinander — Trefferquote (WR) und Expectancy getrennt. Gruppen ohne Daten (n=0) sind Felder, die in den Trades noch nicht erfasst sind.
      </div>
      <div style={s.tableWrap}>
        <div className="table-scroll">
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.thLeft}>Faktor</th>
              <th style={s.thLeft}>Gruppe A</th>
              <th style={s.th}>n</th>
              <th style={s.th}>WR %</th>
              <th style={s.th}>Ø PnL</th>
              <th style={s.thLeft}>Gruppe B</th>
              <th style={s.th}>n</th>
              <th style={s.th}>WR %</th>
              <th style={s.th}>Ø PnL</th>
            </tr>
          </thead>
          <tbody>
            {order.map((fac) => {
              const [a, b] = byFactor[fac];
              return (
                <tr key={fac}>
                  <td style={s.tdLeft}><strong>{FACTOR_LABELS[fac] || fac}</strong></td>
                  <td style={s.tdLeft}>{a?.group ?? '—'}</td>
                  <td style={s.td}>{a?.n ?? 0}</td>
                  <td style={s.td}>{a && a.n > 0 ? pct(a.wr) : '—'}</td>
                  <td style={{ ...s.td, color: a ? pnlColor(a.expectancy) : undefined }}>{a && a.n > 0 ? money(a.expectancy) : '—'}</td>
                  <td style={s.tdLeft}>{b?.group ?? '—'}</td>
                  <td style={s.td}>{b?.n ?? 0}</td>
                  <td style={s.td}>{b && b.n > 0 ? pct(b.wr) : '—'}</td>
                  <td style={{ ...s.td, color: b ? pnlColor(b.expectancy) : undefined }}>{b && b.n > 0 ? money(b.expectancy) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

// ── Sub-Tab: Cluster ─────────────────────────────────────────────────
const Cluster: React.FC<{ report: BacktestReport }> = ({ report }) => (
  <div style={s.card}>
    <div style={s.cardTitle}>Cluster: Asset × Session × Direction</div>
    <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
      Zeilen mit n&lt;5 sind ausgegraut — statistisch nicht belastbar.
    </div>
    <div style={s.tableWrap}>
      <div className="table-scroll">
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.thLeft}>Asset</th>
            <th style={s.thLeft}>Session</th>
            <th style={s.thLeft}>Direction</th>
            <th style={s.th}>n</th>
            <th style={s.th}>Wins</th>
            <th style={s.th}>WR %</th>
            <th style={s.th}>PnL</th>
            <th style={s.th}>Ø PnL/Trade</th>
          </tr>
        </thead>
        <tbody>
          {report.cluster.map((c, i) => (
            <tr key={i} style={c.low_sample ? { opacity: 0.45 } : undefined} title={c.low_sample ? 'statistisch nicht belastbar (n<5)' : undefined}>
              <td style={s.tdLeft}><strong>{c.asset}</strong></td>
              <td style={s.tdLeft}>{c.session}</td>
              <td style={s.tdLeft}>{c.direction === 'short' ? '🔴 short' : '🟢 long'}</td>
              <td style={s.td}>{c.n}{c.low_sample && <span style={{ ...s.badge, background: '#64748b22', color: '#94a3b8' }}>n&lt;5</span>}</td>
              <td style={s.td}>{c.wins}</td>
              <td style={s.td}>{pct(c.wr)}</td>
              <td style={{ ...s.td, color: pnlColor(c.pnl) }}>{money(c.pnl)}</td>
              <td style={{ ...s.td, color: pnlColor(c.expectancy), fontWeight: 700 }}>{money(c.expectancy)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  </div>
);

// ── Sub-Tab: Monatlich ───────────────────────────────────────────────
const Monatlich: React.FC<{ report: BacktestReport }> = ({ report }) => (
  <div style={s.card}>
    <div style={s.cardTitle}>Monatliche Performance</div>
    <div style={s.tableWrap}>
      <div className="table-scroll">
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.thLeft}>Monat</th>
            <th style={s.th}>Trades</th>
            <th style={s.th}>Wins</th>
            <th style={s.th}>WR %</th>
            <th style={s.th}>PnL</th>
            <th style={s.th}>Equity Ende</th>
          </tr>
        </thead>
        <tbody>
          {report.monthly.map((m) => (
            <tr key={m.month}>
              <td style={s.tdLeft}><strong>{m.month}</strong></td>
              <td style={s.td}>{m.trades}</td>
              <td style={s.td}>{m.wins}</td>
              <td style={s.td}>{pct(m.wr)}</td>
              <td style={{ ...s.td, color: pnlColor(m.pnl) }}>{money(m.pnl)}</td>
              <td style={s.td}>{money(m.equity_ende)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  </div>
);

// ── Sub-Tab: Equity ──────────────────────────────────────────────────
const Equity: React.FC<{ report: BacktestReport }> = ({ report }) => {
  const eq = report.equity_curve;
  const startEquity = eq.length ? eq[eq.length - 1].equity - report.kpis.total_pnl : 0;
  const maxEq = Math.max(startEquity, ...eq.map((p) => p.equity));
  const minEq = Math.min(startEquity, ...eq.map((p) => p.equity));
  // Gradient-Offset: grün über Start, rot darunter
  const off = maxEq === minEq ? 0 : (maxEq - startEquity) / (maxEq - minEq);
  return (
    <>
      <div style={s.card}>
        <div style={s.cardTitle}>Equity-Kurve (Start: {money(startEquity)})</div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={eq}>
            <defs>
              <linearGradient id="bt-eq-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor={green} stopOpacity={0.5} />
                <stop offset={off} stopColor={green} stopOpacity={0.1} />
                <stop offset={off} stopColor={red} stopOpacity={0.1} />
                <stop offset={1} stopColor={red} stopOpacity={0.5} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis dataKey="idx" tick={{ fill: '#64748b', fontSize: 11 }} />
            <YAxis domain={['auto', 'auto']} tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => nf(v, 0)} />
            <Tooltip
              contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
              formatter={(v: any) => [money(Number(v)), 'Equity']}
              labelFormatter={(l: any) => `Trade #${l}`}
            />
            <ReferenceLine y={startEquity} stroke="#64748b" strokeDasharray="4 4" />
            <Area type="monotone" dataKey="equity" stroke={green} strokeWidth={2} fill="url(#bt-eq-grad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>Underwater (Drawdown %)</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={eq}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis dataKey="idx" tick={{ fill: '#64748b', fontSize: 11 }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${nf(v, 0)}%`} />
            <Tooltip
              contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
              formatter={(v: any) => [`${nf(Number(v), 2)} %`, 'Drawdown']}
              labelFormatter={(l: any) => `Trade #${l}`}
            />
            <Bar dataKey="dd_pct" fill={red} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>Drawdown-Phasen</div>
        {report.dd_phases.length === 0 ? (
          <div style={s.empty}>Keine Underwater-Phasen im Zeitfenster.</div>
        ) : (
          <div style={s.tableWrap}>
            <div className="table-scroll">
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.thLeft}>Bereich</th>
                  <th style={s.th}>Dauer (Trades)</th>
                  <th style={s.th}>Tiefster DD</th>
                </tr>
              </thead>
              <tbody>
                {[...report.dd_phases].sort((a, b) => a.tiefster_dd - b.tiefster_dd).map((p, i) => (
                  <tr key={i}>
                    <td style={s.tdLeft}>{p.range}</td>
                    <td style={s.td}>{p.dauer_trades}</td>
                    <td style={{ ...s.td, color: red }}>{pct(p.tiefster_dd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ── Sub-Tab: Trades (Sheet-Look, sticky header) ──────────────────────
const TRADE_COLS: [string, string][] = [
  ['ts', 'Zeit'],
  ['asset', 'Asset'],
  ['session', 'Session'],
  ['direction', 'Dir'],
  ['pnl', 'PnL'],
  ['risk_reward', 'RR'],
  ['duration_mins', 'Dauer'],
  ['delta', 'Delta'],
  ['delta_fill', 'Delta Fill'],
  ['aligned', 'Aligned'],
  ['candlesbeforefill', 'Candles'],
  ['mae', 'MAE'],
  ['mae_pct_sl', 'MAE %SL'],
  ['mfi', 'MFI'],
  ['entry_volume', 'Entry Vol'],
];

interface TradesProps {
  rows: Trade[];
  opts: { assets: string[]; sessions: string[]; dirs: string[] };
  fAsset: string; fSession: string; fDir: string;
  setFAsset: (v: string) => void; setFSession: (v: string) => void; setFDir: (v: string) => void;
  sortField: string; sortDir: 'asc' | 'desc'; toggleSort: (f: string) => void;
}
const Trades: React.FC<TradesProps> = ({ rows, opts, fAsset, fSession, fDir, setFAsset, setFSession, setFDir, sortField, sortDir, toggleSort }) => {
  const cell = (t: any, f: string): React.ReactNode => {
    switch (f) {
      case 'ts': return new Date(t.trade_date).toLocaleString('de-DE');
      case 'asset': return (t.asset || '').trim().toUpperCase();
      case 'session': return t.session;
      case 'direction': return (t.direction || 'long') === 'short' ? '🔴 S' : '🟢 L';
      case 'pnl': return <span style={{ color: pnlColor(t.pnl) }}>{money(t.pnl)}</span>;
      case 'risk_reward': return t.risk_reward != null ? nf(t.risk_reward, 2) : '—';
      case 'duration_mins': return t.duration_mins != null ? `${t.duration_mins}` : '—';
      case 'aligned': return t.aligned == null ? '—' : (t.aligned ? '✅' : '—');
      case 'candlesbeforefill': return t.candlesbeforefill != null ? nf(t.candlesbeforefill, 0) : '—';
      default: {
        const v = t[f];
        return v != null && v !== '' ? nf(Number(v), 2) : '—';
      }
    }
  };
  const isLeft = (f: string) => ['ts', 'asset', 'session', 'direction', 'aligned'].includes(f);
  return (
    <div style={s.card}>
      <div style={{ ...s.toolbar, marginBottom: 12 }}>
        <select style={s.select} value={fAsset} onChange={(e) => setFAsset(e.target.value)}>
          <option value="">Alle Assets</option>
          {opts.assets.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={s.select} value={fSession} onChange={(e) => setFSession(e.target.value)}>
          <option value="">Alle Sessions</option>
          {opts.sessions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={s.select} value={fDir} onChange={(e) => setFDir(e.target.value)}>
          <option value="">Alle Directions</option>
          {opts.dirs.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <span style={{ color: '#64748b', fontSize: 12 }}>{rows.length} Trades</span>
      </div>
      <div style={{ ...s.tableWrap, maxHeight: 600, overflowY: 'auto' }}>
        <div className="table-scroll">
        <table style={s.table}>
          <thead>
            <tr>
              {TRADE_COLS.map(([f, lbl]) => (
                <th
                  key={f}
                  style={{ ...(isLeft(f) ? s.thLeft : s.th), position: 'sticky', top: 0, cursor: 'pointer', zIndex: 1 }}
                  onClick={() => toggleSort(f)}
                >
                  {lbl}{sortField === f ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                {TRADE_COLS.map(([f]) => (
                  <td key={f} style={isLeft(f) ? s.tdLeft : s.td}>{cell(t, f)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default BacktestTab;
